/**
 * MonsterPanel Component
 * 
 * Vertical side panel that displays a list of monsters.
 * Styling intentionally reuses the same classes as `MonsterList` so the UI stays consistent.
 */

import React, { useCallback, useMemo } from 'react';
import type { PlayerColor, Theme } from '../../types/game.types';
import type { FrontendMonster } from '../../types/monster.types';
import MonsterCircle from './MonsterCircle';
import { COLORS } from '../../../utils/colors';

import './MonsterPanel.css';

export interface MonsterPanelProps {
    monsters: FrontendMonster[];
    selectedPiece?: FrontendMonster | null;
    mine: boolean;
    playerColor: PlayerColor;
    theme: Theme;
    onMonsterClick?: (monster: FrontendMonster, isMine: boolean) => void;
}

export const MonsterPanel: React.FC<MonsterPanelProps> = ({
    monsters,
    selectedPiece = null,
    mine,
    playerColor,
    theme,
    onMonsterClick,
}) => {
    const sortedMonsters = useMemo(
        () => monsters.slice().sort((a, b) => (mine ? b.id - a.id : a.id - b.id)),
        [monsters, mine]
    );

    const handleClick = useCallback(
        (monster: FrontendMonster) => {
            if (!monster.alive) return; // dead monsters are not interactable
            onMonsterClick?.(monster, mine);
        },
        [mine, onMonsterClick]
    );

    return (
        <div className="monster_panel_container">
            <div className="monster_panel_list" role="list" style={{ justifyContent: mine ? 'flex-end' : 'flex-start' }}>
                {sortedMonsters.map((monster) => {
                    const matchesSelectedPiece =
                        selectedPiece?.type === monster.type && selectedPiece?.player === monster.player;

                    const opacity = monster.alive ? 1 : 0.55;

                    const resolvedBorderColor = matchesSelectedPiece
                        ? COLORS.selected[theme]
                        : playerColor
                            ? COLORS.player_colors[playerColor]
                            : COLORS.player_colors.red;

                    return (
                        <MonsterCircle
                            key={monster.id}
                            monster={monster}
                            theme={theme}
                            disabled={!monster.alive}
                            hidden={!mine}
                            onClick={() => handleClick(monster)}
                            style={{ opacity, cursor: monster.alive ? 'pointer' : 'default' }}
                            imageStyle={{
                                borderColor: resolvedBorderColor,
                            }}
                        />
                    );
                })}
            </div>
        </div>
    );
};

export default MonsterPanel;

